import {
  TallyVendor,
  TallyChartOfAccountTypes,
  TallyVouchersTypes,
} from '../../types/vendor';

const cleanXmlStringFun = (xmlString: string): string => {
  let cleaned = xmlString;

  cleaned = cleaned.replace(/&#([0-8]|1[0-2]|1[4-9]|2[0-9]|3[01]);/g, '');

  cleaned = cleaned.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  cleaned = cleaned.replace(/[\uFFFE\uFFFF]/g, '');

  cleaned = cleaned.replace(/[\uD800-\uDFFF]/g, '');

  cleaned = cleaned.replace(
    /[\u0080-\u009F]/g,
    char => `&#${char.charCodeAt(0)};`,
  );

  return cleaned;
};

export const escapeXml = (unsafe: string = ''): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Escapes a string for use in Tally TDL formulas.
 * Handles special characters like double quotes by using single-quoted strings,
 * and escapes XML entities as well.
 * Also removes control characters that Tally sometimes includes in data.
 */
export const escapeTdlString = (unsafe: string = ''): string => {
  // First remove control characters (like &#4; EOT character) that Tally sometimes includes
  const cleaned = unsafe.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

  // Then escape XML entities
  const xmlEscaped = escapeXml(cleaned);

  // For TDL formulas, if the string contains double quotes, we wrap in single quotes
  // If it contains single quotes, we wrap in double quotes
  // If it contains both, we need to escape the double quotes by doubling them
  const hasDoubleQuote = cleaned.includes('"');
  const hasSingleQuote = cleaned.includes("'");

  if (hasDoubleQuote && !hasSingleQuote) {
    // Use single quotes for the TDL string
    return `'${xmlEscaped.replace(/&quot;/g, '"')}'`;
  } else if (hasSingleQuote && !hasDoubleQuote) {
    // Use double quotes for the TDL string
    return `"${xmlEscaped.replace(/&apos;/g, "'")}"`;
  } else if (hasDoubleQuote && hasSingleQuote) {
    // Both present - escape double quotes by doubling them and use double quotes
    return `"${xmlEscaped.replace(/&quot;/g, '""').replace(/&apos;/g, "'")}"`;
  } else {
    // No quotes - use double quotes
    return `"${xmlEscaped}"`;
  }
};

export const parseCompanyListXml = (xmlString: string): string[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const companyNames = Array.from(xmlDoc.getElementsByTagName('COMPANY'))
    .map(el => el.getAttribute('NAME') || '')
    .map(name => name.trim())
    .filter(name => name !== '');
  return companyNames;
};

export const parseGstNumberXml = (xmlString: string): string | null => {
  // Remove the invalid character before parsing
  const cleanXmlString = cleanXmlStringFun(xmlString);

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(cleanXmlString, 'text/xml');

  // Check for parser errors
  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    console.error('XML parsing error:', parserError.textContent);
    return null;
  }

  const collectionElements = xmlDoc.getElementsByTagName('COLLECTION');

  if (collectionElements.length > 0) {
    const taxUnits = collectionElements[0].getElementsByTagName('TAXUNIT');
    let gstNumber: string | null = null;

    for (let i = 0; i < taxUnits.length; i++) {
      const taxUnit = taxUnits[i];
      const usedForElements = taxUnit.getElementsByTagNameNS('*', 'USEDFOR');

      if (
        usedForElements.length > 0 &&
        usedForElements[0].textContent === 'GST'
      ) {
        const gstRegNumberElements = taxUnit.getElementsByTagNameNS(
          '*',
          'GSTREGNUMBER',
        );
        if (gstRegNumberElements.length > 0) {
          gstNumber = gstRegNumberElements[0].textContent;
          if (gstNumber) {
            gstNumber = gstNumber.trim();
            break;
          }
        }
      }
    }
    return gstNumber;
  }

  return null;
};

export const parseVendorNamesXml = (
  xmlString: string,
): { cleanNames: string[]; corruptedNames: string[] } => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const cleanNames: string[] = [];
  const corruptedNames: string[] = [];

  const ledgerElements = xmlDoc.querySelectorAll('LEDGER');
  ledgerElements.forEach(ledger => {
    const nameAttribute = ledger.getAttribute('NAME');
    if (nameAttribute) {
      // Check for control characters (excluding common whitespace like tab, newline, carriage return)
      // and also check for the specific XML entities if they somehow remain unparsed or are part of the corruption
      const isCorrupted = /[\x00-\x1F\uFFFD]/.test(nameAttribute);

      if (isCorrupted) {
        corruptedNames.push(nameAttribute);
      } else {
        cleanNames.push(nameAttribute || '');
      }
    }
  });

  return { cleanNames, corruptedNames };
};

export const parseSingleVendorDetailsXml = (
  xmlString: string,
  companyName: string,
  isDebtor: boolean = false,
): TallyVendor | null => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const ledgerElement = xmlDoc.querySelector('COLLECTION LEDGER');
  if (!ledgerElement) {
    return null;
  }

  const name = (ledgerElement.getAttribute('NAME') || '').trim();

  let gstNumber: string | null = null;
  const gstDetailsList = ledgerElement.querySelectorAll(
    'LEDGSTREGDETAILS\\.LIST',
  );

  gstDetailsList.forEach(gstDetail => {
    const gstinElement = gstDetail.querySelector('GSTIN');
    if (gstinElement && gstinElement.textContent) {
      const currentGstin = gstinElement.textContent.trim();
      if (currentGstin !== '') {
        gstNumber = currentGstin;
      }
    }
  });

  if (name) {
    // Default voucher type based on vendor category:
    // - Debtors (customers) → Sales voucher
    // - Creditors (vendors) → Purchase voucher
    const defaultVoucherName = isDebtor
      ? TallyVouchersTypes.sales.toString()
      : TallyVouchersTypes.purchase.toString();

    return {
      companyName: companyName,
      vendorName: name,
      vendorGST: gstNumber,
      chartOfAccount: TallyChartOfAccountTypes.abnormal.toString(),
      voucherName: defaultVoucherName,
      tdsRate: 0,
      tdsThreshold: 0,
      tdsResidual: 0,
    };
  }
  return null;
};

export const parseActiveCompanyNameXml = (xmlString: string): string | null => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const companyElement = xmlDoc.querySelector('COLLECTION > COMPANY');
  if (companyElement) {
    const companyName = companyElement.getAttribute('NAME');
    return companyName ? companyName.trim() : null;
  }
  return null;
};

export const parseVoucherTypesXml = (xmlString: string): string[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const collectionElements = xmlDoc.getElementsByTagName('COLLECTION');

  if (collectionElements.length > 0) {
    const voucherTypes = Array.from(
      collectionElements[0].getElementsByTagName('VOUCHERTYPE'),
    )
      .map(el => el.getAttribute('NAME') || '')
      .map(name => name.trim())
      .filter(name => name !== '');

    return voucherTypes;
  }

  return [];
};

export const parsePurchaseAndSalesLedgersXml = (
  xmlString: string,
): string[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const ledgerNames: string[] = [];

  const ledgerElements = xmlDoc.querySelectorAll('LEDGER');
  ledgerElements.forEach(ledger => {
    const nameAttribute = ledger.getAttribute('NAME');
    if (nameAttribute) {
      ledgerNames.push(nameAttribute || '');
    }
  });

  return ledgerNames;
};

export const parseVoucherRegistersXml = (
  xmlString: string,
  defaultVoucherName: string,
  purchaseAccountsLedgers: string[],
  salesAccountsLedgers: string[],
  directExpensesAccountsLedgers: string[],
  indirectExpensesAccountsLedgers: string[],
  indirectIncomesAccountsLedgers: string[],
  directIncomesAccountsLedgers: string[],
  journalAccountsLedgers: string[] = [],
): { vendorName: string; voucherName: string; chartOfAccount: string }[] => {
  const parser = new DOMParser();
  const cleanedXmlString = cleanXmlStringFun(xmlString);
  const xmlDoc = parser.parseFromString(cleanedXmlString, 'text/xml');
  const voucherEntries = xmlDoc.getElementsByTagName('VOUCHER');

  const result: {
    vendorName: string;
    voucherName: string;
    chartOfAccount: string;
  }[] = [];
  const uniqueVendorNames = new Set<string>();
  const purchaseLedgersSet = new Set(purchaseAccountsLedgers);
  const salesLedgersSet = new Set(salesAccountsLedgers);
  const directExpensesSet = new Set(
    directExpensesAccountsLedgers.map(name => name.trim()),
  );
  const indirectExpensesSet = new Set(
    indirectExpensesAccountsLedgers.map(name => name.trim()),
  );
  const indirectIncomesSet = new Set(
    indirectIncomesAccountsLedgers.map(name => name.trim()),
  );
  const directIncomesSet = new Set(
    directIncomesAccountsLedgers.map(name => name.trim()),
  );
  const journalLedgersSet = new Set(
    journalAccountsLedgers.map(name => name.trim()),
  );

  for (let i = 0; i < voucherEntries.length; i++) {
    const voucherEntry = voucherEntries[i];

    const partyLedgerNameElement =
      voucherEntry.getElementsByTagName('PARTYLEDGERNAME')[0];
    const vendorName = partyLedgerNameElement
      ? (partyLedgerNameElement.textContent || '').trim()
      : '';

    if (!vendorName || uniqueVendorNames.has(vendorName)) {
      continue;
    }

    uniqueVendorNames.add(vendorName);

    const voucherTypeNameElement =
      voucherEntry.getElementsByTagName('VOUCHERTYPENAME')[0];
    const voucherName = voucherTypeNameElement
      ? (voucherTypeNameElement.textContent || '').trim()
      : defaultVoucherName;

    let chartOfAccount = TallyChartOfAccountTypes.abnormal.toString();

    const inventoryAllocations = voucherEntry.getElementsByTagName(
      'ACCOUNTINGALLOCATIONS.LIST',
    );
    const ledgerEntries =
      voucherEntry.getElementsByTagName('LEDGERENTRIES.LIST');
    const allLedgerEntries = voucherEntry.getElementsByTagName(
      'ALLLEDGERENTRIES.LIST',
    );

    const allAllocations = [
      ...Array.from(inventoryAllocations),
      ...Array.from(ledgerEntries),
      ...Array.from(allLedgerEntries),
    ];

    for (let j = 0; j < allAllocations.length; j++) {
      const allocation = allAllocations[j];
      const ledgerNameElement =
        allocation.getElementsByTagName('LEDGERNAME')[0];

      if (ledgerNameElement) {
        const ledgerName = (ledgerNameElement.textContent || '').trim();
        // Skip if ledger name is same as vendor name (party ledger)
        if (ledgerName === vendorName) {
          continue;
        }

        if (purchaseLedgersSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (salesLedgersSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (directExpensesSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (indirectExpensesSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (indirectIncomesSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (directIncomesSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        if (journalLedgersSet.has(ledgerName)) {
          chartOfAccount = ledgerName;
          break;
        }
        // For Journal vouchers, if no specific group matches, use the ledger name directly
        // This captures the contra ledger in journal entries
        if (
          voucherName.toLowerCase() === 'journal' &&
          chartOfAccount === TallyChartOfAccountTypes.abnormal.toString()
        ) {
          chartOfAccount = ledgerName;
          // Don't break here - continue looking for a more specific match
        }
      }
    }

    result.push({ vendorName, voucherName, chartOfAccount });
  }

  return result;
};
